import { useState, useEffect } from "react";
import { Modal, Tabs, Form, Input, Button, Avatar, Tag, Row, Col, Divider } from "antd";
import { useDispatch, useSelector } from "react-redux";
import SVGIcon from "../../../../utils/SVGIcon";
import { Notification } from "../../../../config/Global";
import {
  updateUserProfile,
  changeUserPassword,
  getUserProfileData,
} from "../../../../features/auth/authSlice";
import { resetStore } from "../../../../features/auth/apiService";
import { CONSTANT } from "../../../../config/Constant";

const UserProfileModal = ({ open, close, activeTab = "1" }) => {
  const dispatch = useDispatch();
  const { userProfileData } = useSelector(
    (state) => state.auth
  );
  const isAdmin = localStorage.getItem("isa") || "false";

  const [currentTab, setCurrentTab] = useState(activeTab);
  const [profileForm] = Form.useForm();
  const [passwordForm] = Form.useForm();

  const [profileLoading, setProfileLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [isProfileDirty, setIsProfileDirty] = useState(false);
  const [isPasswordDirty, setIsPasswordDirty] = useState(false);

  const getInitials = (name) => {
    if (!name) return "U";
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) {
      return parts[0].substring(0, 2).toUpperCase();
    }
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  };

  const userName = userProfileData?.userName || "";
  const userEmail = userProfileData?.email || "";
  const roleTitle =
    userProfileData?.roleName ||
    userProfileData?.role?.name ||
    (isAdmin === "true" ? "Super Admin" : "User");

  const getInitialNames = () => {
    let fName = userProfileData?.firstName || "";
    let lName = userProfileData?.lastName || "";
    if (!fName && userProfileData?.userName) {
      const parts = userProfileData.userName.trim().split(/\s+/);
      fName = parts[0] || "";
      lName = parts.slice(1).join(" ") || "";
    }
    return { firstName: fName, lastName: lName };
  };

  useEffect(() => {
    if (open) {
      setCurrentTab(activeTab);
      const names = getInitialNames();
      profileForm.setFieldsValue({
        firstName: names.firstName,
        lastName: names.lastName,
      });
      passwordForm.resetFields();
      setIsProfileDirty(false);
      setIsPasswordDirty(false);
    }
  }, [open, activeTab, userProfileData]);

  const handleProfileValuesChange = (_, allValues) => {
    const initial = getInitialNames();
    const isChanged =
      (allValues.firstName || "").trim() !== initial.firstName.trim() ||
      (allValues.lastName || "").trim() !== initial.lastName.trim();
    setIsProfileDirty(isChanged);
  };

  const handlePasswordValuesChange = () => {
    const values = passwordForm.getFieldsValue();
    const hasValues =
      !!values.currentPassword &&
      !!values.newPassword &&
      !!values.confirmPassword;
    setIsPasswordDirty(hasValues);
  };

  const handleUpdateProfile = async (values) => {
    try {
      setProfileLoading(true);
      const payload = {
        firstName: values.firstName.trim(),
        lastName: values.lastName.trim(),
      };
      await dispatch(updateUserProfile(payload)).unwrap();
      Notification.success({
        message: "Profile updated successfully.",
      });
      const backOfficerId =
        localStorage.getItem("backOfficerId") || localStorage.getItem("userId");
      if (backOfficerId) {
        const profilePayload =
          isAdmin === "true"
            ? { id: backOfficerId }
            : {
                entityId: backOfficerId,
                userType: CONSTANT.USER_TYPE.BACK_OFFICE,
              };
        dispatch(getUserProfileData(profilePayload));
      }
      setIsProfileDirty(false);
      close();
    } catch (err) {
      Notification.error({
        message: err?.message || err || "Failed to update profile.",
      });
    } finally {
      setProfileLoading(false);
    }
  };

  const handleChangePassword = async (values) => {
    try {
      setPasswordLoading(true);
      const payload = {
        currentPassword: values.currentPassword,
        newPassword: values.newPassword,
        confirmPassword: values.confirmPassword,
      };
      const res = await dispatch(changeUserPassword(payload)).unwrap();
      Notification.success({
        message:
          res?.message || "Password changed successfully. Please log in again.",
      });
      passwordForm.resetFields();
      close();
      setTimeout(() => {
        resetStore();
      }, 1000);
    } catch (err) {
      Notification.error({
        message: err?.message || err || "Failed to change password.",
      });
    } finally {
      setPasswordLoading(false);
    }
  };

  const tabItems = [
    {
      key: "1",
      forceRender: true,
      label: (
        <div className="profileTabLabel">
          <SVGIcon icon="user" size={16} />
          <span>Profile Information</span>
        </div>
      ),
      children: (
        <div className="userProfileContent">
          <div>
            {/* User Profile Header Box */}
            <div className="profileModalHeader">
              <div className="profileAvatarWrap">
                <Avatar size={64} className="profileAvatar initialsAvatar">
                  {getInitials(userName)}
                </Avatar>
              </div>
              <div className="profileHeaderMeta">
                <h4 className="profileName">{userName}</h4>
                <span className="profileEmail">{userEmail}</span>
                {roleTitle && (
                  <div className="profileTags">
                    <Tag color="blue" className="roleTag">
                      <SVGIcon icon="userRole" size={12} />
                      <span>{roleTitle}</span>
                    </Tag>
                  </div>
                )}
              </div>
            </div>

            <Form
              form={profileForm}
              layout="vertical"
              onFinish={handleUpdateProfile}
              onValuesChange={handleProfileValuesChange}
              autoComplete="off"
              requiredMark={false}
              style={{ marginTop: 20 }}
            >
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="firstName"
                    label={
                      <span>
                        <span style={{ color: "#ff4d4f", marginRight: 4 }}>*</span>
                        First Name
                      </span>
                    }
                    rules={[
                      { required: true, message: "Please enter first name" },
                      {
                        whitespace: true,
                        message: "First name cannot be empty",
                      },
                    ]}
                  >
                    <Input placeholder="Enter First Name" size="large" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="lastName"
                    label={
                      <span>
                        <span style={{ color: "#ff4d4f", marginRight: 4 }}>*</span>
                        Last Name
                      </span>
                    }
                    rules={[
                      { required: true, message: "Please enter last name" },
                      {
                        whitespace: true,
                        message: "Last name cannot be empty",
                      },
                    ]}
                  >
                    <Input placeholder="Enter Last Name" size="large" />
                  </Form.Item>
                </Col>
              </Row>
            </Form>
          </div>
        </div>
      ),
    },
    {
      key: "2",
      forceRender: true,
      label: (
        <div className="profileTabLabel">
          <SVGIcon icon="password" size={16} />
          <span>Security & Password</span>
        </div>
      ),
      children: (
        <div className="userProfileContent securityTabContent">
          <div>
            <div className="profileModalHeader">
              <div className="securityIconWrap">
                <SVGIcon icon="password" size={28} />
              </div>
              <div className="profileHeaderMeta">
                <h4 className="profileName">Account Security & Credentials</h4>
                <span className="profileEmail">
                  Manage your login password and account credentials.
                </span>
              </div>
            </div>
            <Divider />
            {/* Security Notice Box */}
            <div className="securityNotice" style={{ marginBottom: 16 }}>
              <SVGIcon icon="warning" size={16} />
              <span>
                Ensure your new password contains at least 8 characters, including
                uppercase, lowercase letters, and numbers.
              </span>
            </div>

            <Form
              form={passwordForm}
              layout="vertical"
              onFinish={handleChangePassword}
              onValuesChange={handlePasswordValuesChange}
              autoComplete="off"
              requiredMark={false}
            >
              <Form.Item
                name="currentPassword"
                style={{ marginBottom: 14 }}
                label={
                  <span>
                    <span style={{ color: "#ff4d4f", marginRight: 4 }}>*</span>
                    Current Password
                  </span>
                }
                rules={[
                  { required: true, message: "Please enter current password" },
                ]}
              >
                <Input.Password
                  placeholder="Enter Current Password"
                  size="large"
                />
              </Form.Item>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="newPassword"
                    style={{ marginBottom: 14 }}
                    label={
                      <span>
                        <span style={{ color: "#ff4d4f", marginRight: 4 }}>*</span>
                        New Password
                      </span>
                    }
                    rules={[
                      { required: true, message: "Please enter new password" },
                      {
                        pattern: /(?=.*\d)(?=.*[a-z])(?=.*[A-Z]).{8,}/,
                        message:
                          "Password must contain at least one number and one uppercase and lowercase letter, and at least 8 or more characters",
                      },
                    ]}
                  >
                    <Input.Password
                      placeholder="Enter New Password"
                      size="large"
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="confirmPassword"
                    style={{ marginBottom: 14 }}
                    label={
                      <span>
                        <span style={{ color: "#ff4d4f", marginRight: 4 }}>*</span>
                        Confirm New Password
                      </span>
                    }
                    dependencies={["newPassword"]}
                    rules={[
                      {
                        required: true,
                        message: "Please confirm your new password",
                      },
                      ({ getFieldValue }) => ({
                        validator(_, value) {
                          if (!value || getFieldValue("newPassword") === value) {
                            return Promise.resolve();
                          }
                          return Promise.reject(
                            new Error("The two passwords do not match")
                          );
                        },
                      }),
                    ]}
                  >
                    <Input.Password
                      placeholder="Confirm New Password"
                      size="large"
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Form>
          </div>
        </div>
      ),
    },
  ];

  return (
    <Modal
      title="User Profile"
      width={680}
      open={open}
      onCancel={close}
      centered
      closeIcon={<SVGIcon icon="cross" />}
      className="userProfileModal"
      destroyOnClose
      footer={[
        <Button
          key="cancel"
          size="large"
          onClick={close}
          style={{
            minWidth: 120,
            height: 40,
            fontWeight: 600,
            borderRadius: 6,
            color: "#0958d9",
            borderColor: "#0958d9",
          }}
        >
          CANCEL
        </Button>,
        currentTab === "1" ? (
          <Button
            key="saveProfile"
            type="primary"
            size="large"
            loading={profileLoading}
            disabled={!isProfileDirty || profileLoading}
            onClick={() => profileForm.submit()}
            style={{
              minWidth: 150,
              height: 40,
              fontWeight: 600,
              borderRadius: 6,
              textTransform: "uppercase",
            }}
          >
            SAVE CHANGES
          </Button>
        ) : (
          <Button
            key="saveSecurity"
            type="primary"
            size="large"
            loading={passwordLoading}
            disabled={!isPasswordDirty || passwordLoading}
            onClick={() => passwordForm.submit()}
            style={{
              minWidth: 170,
              height: 40,
              fontWeight: 600,
              borderRadius: 6,
              textTransform: "uppercase",
            }}
          >
            UPDATE PASSWORD
          </Button>
        ),
      ]}
    >
      <Tabs
        activeKey={currentTab}
        onChange={(key) => setCurrentTab(key)}
        items={tabItems}
        className="profileTabs"
        animated={false}
        destroyInactiveTabPane={false}
      />
    </Modal>
  );
};

export default UserProfileModal;
